import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initTheme } from './utils/theme';

// 初始化主题
initTheme();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// 注册自定义 Service Worker，用于 PWA 支持（仅在支持的浏览器中生效）
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // 检查用户是否已经刷新过（在整个会话期间不再检测更新）
    const hasUserRefreshed = (): boolean => {
      try {
        return sessionStorage.getItem('sw_user_has_refreshed') === 'true';
      } catch {
        return false;
      }
    };

    // 检查是否是刷新后的首次加载（用户主动刷新）
    const isRefreshAfterUpdate = (): boolean => {
      try {
        const isRefreshing = sessionStorage.getItem('sw_refreshing') === 'true';
        if (isRefreshing) {
          // 清除刷新标记
          sessionStorage.removeItem('sw_refreshing');
          // 标记用户已经刷新过（整个会话期间不再检测更新）
          sessionStorage.setItem('sw_user_has_refreshed', 'true');
          // 清除更新标志（刷新后应该清除，因为用户已经刷新了）
          localStorage.removeItem('sw_has_update_available');
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

    // 设置新版本可用标志（用于在设置页面显示提醒）
    const setUpdateAvailable = (value: boolean): void => {
      try {
        if (value) {
          localStorage.setItem('sw_has_update_available', 'true');
        } else {
          localStorage.removeItem('sw_has_update_available');
        }
      } catch (error) {
        console.warn('[Service Worker] Failed to set update flag:', error);
      }
    };

    // 检查用户是否已经刷新过（在注册前检查，避免注册不必要的监听器）
    const userHasRefreshed = hasUserRefreshed();
    const isAfterRefresh = isRefreshAfterUpdate();

    // 如果用户已经刷新过，整个会话期间都不再检测更新
    if (userHasRefreshed) {
      console.log('[Service Worker] User has refreshed, skipping all update checks for this session');
      // 仍然注册 Service Worker，但不进行更新检查
      navigator.serviceWorker
        .register('/service-worker.js', {
          updateViaCache: 'none'
        })
        .then((registration) => {
          console.log('[Service Worker] Registered (update checks disabled):', registration.scope);
        })
        .catch((error) => {
          console.error('Service worker registration failed:', error);
        });
      return; // 不进行任何更新检查
    }

    // 如果是刷新后的首次加载，不检查更新（避免立即检测到更新）
    if (isAfterRefresh) {
      console.log('[Service Worker] Skipping update check after refresh');
      // 仍然注册 Service Worker，但不进行更新检查
      navigator.serviceWorker
        .register('/service-worker.js', {
          updateViaCache: 'none'
        })
        .then((registration) => {
          console.log('[Service Worker] Registered (update check skipped after refresh):', registration.scope);
        })
        .catch((error) => {
          console.error('Service worker registration failed:', error);
        });
      return; // 不进行更新检查
    }

    // 只在首次打开页面时检查一次更新
    // 记录注册时是否已有 controller（用于区分首次安装和更新）
    const hadControllerBeforeRegister = !!navigator.serviceWorker.controller;

    navigator.serviceWorker
      .register('/service-worker.js', {
        // 每次检查更新
        updateViaCache: 'none'
      })
      .then((registration) => {
        console.log('[Service Worker] Registered:', registration.scope);
        console.log('[Service Worker] Had controller before register:', hadControllerBeforeRegister);

        // 只在首次打开页面时检查一次更新
        console.log('[Service Worker] Checking for updates on page load');
        registration.update().catch((err) => {
          console.warn('[Service Worker] Update check failed:', err);
        });

        // 检查是否有更新
        registration.addEventListener('updatefound', () => {
          // 防呆检查：如果用户已经刷新过，直接返回（防止事件晚到的情况）
          if (hasUserRefreshed()) {
            console.log('[Service Worker] User has refreshed, ignoring updatefound event');
            return;
          }

          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // 新版本已安装，设置标志供设置页面显示提醒
                console.log('[Service Worker] New version available');
                
                // 再次检查用户是否已经刷新过（防止在检查过程中用户刷新了）
                if (hasUserRefreshed()) {
                  console.log('[Service Worker] User has refreshed, skipping update flag');
                  return;
                }
                
                // 设置新版本可用标志
                setUpdateAvailable(true);
              }
            });
          }
        });
      })
      .catch((error) => {
        // 这里静默失败即可，不影响正常功能
        console.error('Service worker registration failed:', error);
      });

    // 监听 Service Worker 消息（来自 activate 事件的通知）
    // 只在首次打开页面时注册（用户刷新后不再注册）
    if (!userHasRefreshed && !isAfterRefresh) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('[Service Worker] Updated to version:', event.data.version);
          
          // 防呆检查：如果用户已经刷新过，忽略消息
          if (hasUserRefreshed()) {
            console.log('[Service Worker] User has refreshed, ignoring SW_UPDATED message');
            return;
          }

          // 关键修复：如果是首次安装（注册时没有 controller），不应该视为更新
          if (!hadControllerBeforeRegister) {
            console.log('[Service Worker] First installation, not an update, ignoring SW_UPDATED message');
            return;
          }
          
          // 设置新版本可用标志
          setUpdateAvailable(true);
        }
      });
    }
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
